import React, { useId, useState } from 'react';
import { Building2, Search, UserRound } from 'lucide-react';
import type {
  UpdateUserAccountTypeRequest,
  UserAccountType,
  UserProfile,
} from '../services/api.types';
import { lookupCompanyByTaxId } from '../services/user.api';
import { useTranslations } from '../translations';
import './AccountTypeForm.css';

export type AccountTypeFormErrors = Partial<Record<keyof UpdateUserAccountTypeRequest, boolean>>;

export const emptyAccountTypeForm = (
  accountType: UserAccountType = 'PRIVATE'
): UpdateUserAccountTypeRequest => ({
  accountType,
  declarationAccepted: false,
  companyLegalName: '',
  companyLegalForm: '',
  taxIdentificationNumber: '',
  tradeRegisterNumber: '',
  registeredOffice: '',
  companyEmail: '',
  companyPhone: '',
  vatRegistered: null,
});

export const accountTypeFormFromProfile = (profile: UserProfile): UpdateUserAccountTypeRequest => ({
  accountType: profile.accountType ?? 'PRIVATE',
  declarationAccepted: Boolean(profile.accountTypeDeclaredAt),
  companyLegalName: profile.companyLegalName ?? '',
  companyLegalForm: profile.companyLegalForm ?? '',
  taxIdentificationNumber: profile.taxIdentificationNumber ?? '',
  tradeRegisterNumber: profile.tradeRegisterNumber ?? '',
  registeredOffice: profile.registeredOffice ?? '',
  companyEmail: profile.companyEmail ?? '',
  companyPhone: profile.companyPhone ?? '',
  vatRegistered: profile.vatRegistered ?? null,
});

export const validateAccountTypeForm = (
  value: UpdateUserAccountTypeRequest
): AccountTypeFormErrors => {
  const errors: AccountTypeFormErrors = {};
  if (!value.declarationAccepted) errors.declarationAccepted = true;
  if (value.accountType === 'COMPANY') {
    const required: Array<keyof UpdateUserAccountTypeRequest> = [
      'companyLegalName',
      'companyLegalForm',
      'taxIdentificationNumber',
      'registeredOffice',
      'companyEmail',
      'companyPhone',
    ];
    required.forEach((field) => {
      if (!String(value[field] ?? '').trim()) errors[field] = true;
    });
    if (value.vatRegistered === null) errors.vatRegistered = true;
    if (value.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.companyEmail)) {
      errors.companyEmail = true;
    }
  }
  return errors;
};

interface AccountTypeFormProps {
  value: UpdateUserAccountTypeRequest;
  onChange: (value: UpdateUserAccountTypeRequest) => void;
  selectedAccountType?: UserAccountType | null;
  onTypeSelect?: (accountType: UserAccountType) => void;
  disabled?: boolean;
  errors?: AccountTypeFormErrors;
}

const AccountTypeForm: React.FC<AccountTypeFormProps> = ({
  value,
  onChange,
  selectedAccountType = value.accountType,
  onTypeSelect,
  disabled,
  errors = {},
}) => {
  const t = useTranslations();
  const id = useId();
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  const update = <K extends keyof UpdateUserAccountTypeRequest>(
    field: K,
    fieldValue: UpdateUserAccountTypeRequest[K]
  ) => onChange({ ...value, [field]: fieldValue });

  const selectType = (accountType: UserAccountType) => {
    onChange({ ...value, accountType, declarationAccepted: false });
    onTypeSelect?.(accountType);
    setLookupMessage(null);
  };

  const lookupCompany = async () => {
    const taxId = value.taxIdentificationNumber.trim();
    if (!taxId || isLookingUp) return;
    setIsLookingUp(true);
    setLookupMessage(null);
    try {
      const result = await lookupCompanyByTaxId(taxId);
      onChange({
        ...value,
        companyLegalName: result.companyLegalName ?? value.companyLegalName,
        companyLegalForm: result.companyLegalForm ?? value.companyLegalForm,
        taxIdentificationNumber: result.taxIdentificationNumber,
        tradeRegisterNumber: result.tradeRegisterNumber ?? value.tradeRegisterNumber,
        registeredOffice: result.registeredOffice ?? value.registeredOffice,
        companyPhone: result.companyPhone ?? value.companyPhone,
        vatRegistered: result.vatRegistered,
      });
      setLookupMessage({
        type: result.fiscallyInactive ? 'warning' : 'success',
        text: result.fiscallyInactive ? t.accountType.companyInactive : t.accountType.companyLookupSuccess,
      });
    } catch (error) {
      console.error('Error looking up company:', error);
      setLookupMessage({ type: 'error', text: t.accountType.companyLookupError });
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <div className="account-type-form">
      <div className="account-type-form-options" role="radiogroup" aria-label={t.accountType.title}>
        <button
          type="button"
          role="radio"
          aria-checked={selectedAccountType === 'PRIVATE'}
          className={`account-type-form-option${selectedAccountType === 'PRIVATE' ? ' selected' : ''}`}
          onClick={() => selectType('PRIVATE')}
          disabled={disabled}
        >
          <span className="account-type-form-option-icon" aria-hidden><UserRound size={20} /></span>
          <span><strong>{t.accountType.privateLabel}</strong><small>{t.accountType.privateDescription}</small></span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={selectedAccountType === 'COMPANY'}
          className={`account-type-form-option${selectedAccountType === 'COMPANY' ? ' selected' : ''}`}
          onClick={() => selectType('COMPANY')}
          disabled={disabled}
        >
          <span className="account-type-form-option-icon" aria-hidden><Building2 size={20} /></span>
          <span><strong>{t.accountType.companyLabel}</strong><small>{t.accountType.companyDescription}</small></span>
        </button>
      </div>

      {value.accountType === 'COMPANY' && (
        <div className="account-type-company-fields">
          <p className="account-type-company-intro">{t.accountType.companyDetailsDescription}</p>
          <div className="account-type-field account-type-tax-field">
            <label htmlFor={`${id}-tax-id`}>{t.accountType.taxId}</label>
            <div className="account-type-tax-row">
              <input
                id={`${id}-tax-id`}
                value={value.taxIdentificationNumber}
                onChange={(event) => update('taxIdentificationNumber', event.target.value.toUpperCase())}
                placeholder={t.accountType.taxIdPlaceholder}
                maxLength={12}
                className={errors.taxIdentificationNumber ? 'invalid' : ''}
                disabled={disabled || isLookingUp}
              />
              <button type="button" onClick={() => void lookupCompany()} disabled={disabled || isLookingUp || !value.taxIdentificationNumber.trim()}>
                <Search size={17} aria-hidden />
                {isLookingUp ? t.accountType.companyLookingUp : t.accountType.companyLookup}
              </button>
            </div>
            <small>{t.accountType.companyLookupHint}</small>
            {lookupMessage && <span className={`account-type-lookup-message ${lookupMessage.type}`} role="status">{lookupMessage.text}</span>}
          </div>

          <div className="account-type-fields-grid">
            <Field id={`${id}-legal-name`} label={t.accountType.legalName} value={value.companyLegalName} onChange={(text) => update('companyLegalName', text)} invalid={errors.companyLegalName} disabled={disabled} />
            <Field id={`${id}-legal-form`} label={t.accountType.legalForm} value={value.companyLegalForm} onChange={(text) => update('companyLegalForm', text)} invalid={errors.companyLegalForm} disabled={disabled} />
            <Field id={`${id}-register-number`} label={t.accountType.tradeRegisterNumberOptional} value={value.tradeRegisterNumber} onChange={(text) => update('tradeRegisterNumber', text)} invalid={errors.tradeRegisterNumber} disabled={disabled} />
            <Field id={`${id}-company-email`} label={t.accountType.companyEmail} value={value.companyEmail} onChange={(text) => update('companyEmail', text)} invalid={errors.companyEmail} disabled={disabled} type="email" />
            <Field id={`${id}-company-phone`} label={t.accountType.companyPhone} value={value.companyPhone} onChange={(text) => update('companyPhone', text)} invalid={errors.companyPhone} disabled={disabled} type="tel" />
          </div>

          <div className="account-type-field">
            <label htmlFor={`${id}-office`}>{t.accountType.registeredOffice}</label>
            <textarea id={`${id}-office`} value={value.registeredOffice} onChange={(event) => update('registeredOffice', event.target.value)} className={errors.registeredOffice ? 'invalid' : ''} maxLength={500} disabled={disabled} />
          </div>

          <fieldset className={`account-type-vat${errors.vatRegistered ? ' invalid' : ''}`}>
            <legend>{t.accountType.vatRegistered}</legend>
            <label><input type="radio" name={`${id}-vat`} checked={value.vatRegistered === true} onChange={() => update('vatRegistered', true)} disabled={disabled} />{t.accountType.yes}</label>
            <label><input type="radio" name={`${id}-vat`} checked={value.vatRegistered === false} onChange={() => update('vatRegistered', false)} disabled={disabled} />{t.accountType.no}</label>
          </fieldset>
        </div>
      )}

      <label className={`account-type-declaration${errors.declarationAccepted ? ' invalid' : ''}`}>
        <input type="checkbox" checked={value.declarationAccepted} onChange={(event) => update('declarationAccepted', event.target.checked)} disabled={disabled} />
        <span>{value.accountType === 'COMPANY' ? t.accountType.companyDeclaration : t.accountType.privateDeclaration}</span>
      </label>
    </div>
  );
};

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  type?: 'text' | 'email' | 'tel';
}

const Field: React.FC<FieldProps> = ({ id, label, value, onChange, invalid, disabled, type = 'text' }) => (
  <div className="account-type-field">
    <label htmlFor={id}>{label}</label>
    <input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} className={invalid ? 'invalid' : ''} maxLength={255} disabled={disabled} />
  </div>
);

export default AccountTypeForm;
